import { useFormik } from "formik";
import * as yup from "yup";
import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import PhoneInput from "react-phone-input-2";
import { FormHelperText } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createCustomerUser } from "../../../../services/api/client";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../../../store/toast/toastSlice";
import { useDispatch } from "react-redux";

const validationSchema = yup.object({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  email: yup.string().email("Enter a valid email").required("Email is required"),
  phone: yup.string().required("Phone is required"),
});

const GenerateSignupModal = ({
  open,
  close,
  selectedClient,
  clientId: clientIdProp,
  onSuccess,
}) => {
  const { id: paramId } = useParams();
  const id = clientIdProp || selectedClient?.id || paramId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutateAsync: createCustomerMutation, isPending: isLoading } = useMutation({
    mutationFn: createCustomerUser,
  });

  // Prepopulate the form with client data if available. The email starts on the
  // company's own address but stays editable: what is typed here becomes the
  // user's login address, while the company keeps the one on its record.
  const initialValues = {
    firstName: "",
    lastName: "",
    email: selectedClient?.email || "",
    phone: selectedClient?.phoneNumber || "",
  };

  // Separate submit handler
  const handleSubmit = async (values) => {
    try {
      await createCustomerMutation({ id, data: values });
      await queryClient.invalidateQueries({ queryKey: ["client", String(id)] });
      dispatch(
        showToast({
          message: "Customer User generated successfully",
          type: "success",
        }),
      );
      close(false);
      if (onSuccess) onSuccess();
      else navigate("/workBoard/clients");
    } catch (err) {
      console.error("Failed to create customer:", err);
      dispatch(
        showToast({
          message: err?.response?.data?.message || err?.message || "Failed to create customer user",
          type: "error",
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true, // important to update initialValues when selectedClient changes
    validateOnChange: true,
    validateOnBlur: true,
  });

  return (
    <form>
      <BaseModal
        open={open}
        close={() => close(false)}
        className="-top-[260px] overflow-auto"
        style={{ width: "1000px" }}
      >
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">Generate Signup</h6>
        </div>

        <div className="px-8 py-4 grid grid-cols-2 gap-x-6 gap-y-4">
          <BaseTextField
            label="Email"
            labelShrink={false}
            requirement="required"
            id="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <BaseTextField
            label="First Name"
            labelShrink={false}
            id="firstName"
            name="firstName"
            requirement="required"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />

          <BaseTextField
            label="Last Name"
            labelShrink={false}
            id="lastName"
            name="lastName"
            requirement="required"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />

          <div>
            <label htmlFor="phone" className="block text-sm font-sm mb-[10px] text-[#505050]">
              Phone
              <span className="text-red-500 ml-0.5" aria-hidden="true">
                *
              </span>
            </label>
            <div className="relative w-full">
              <PhoneInput
                inputClass={`!w-full !h-[42px] !pl-12 !pr-3 !text-sm !font-light !text-black !bg-white !border !rounded-md !shadow-md ${
                  formik.touched.phone && formik.errors.phone && "!border-gray-300"
                } focus:!outline-none focus:!ring-1 focus:!ring-orange-400`}
                buttonClass="!border-none !bg-transparent"
                containerClass="!w-full"
                specialLabel=""
                country={"au"}
                value={formik.values.phone}
                onChange={(value) => formik.setFieldValue("phone", value)}
                onBlur={() => formik.setFieldTouched("phone", true)}
                inputProps={{ name: "phone", id: "phone", type: "tel" }}
              />
              {formik.touched.phone && formik.errors.phone && (
                <FormHelperText error>{formik.errors.phone}</FormHelperText>
              )}
            </div>
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            disabled={Object.keys(formik.errors).length > 0}
            loaderSize="22px"
            isLoading={isLoading}
            color="primary"
            className="py-[5px] px-[20px] min-w-32"
            type="submit"
            onClick={formik.handleSubmit}
          >
            Generate
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
};

export default GenerateSignupModal;
